/* ---------------------------------------------------------------------------
   Icons for a defect's category and severity.

   Drawn on the same 24-unit stroke grid as ICONS in src/core/dom.ts, so they sit
   beside every other icon in the application at the same weight.

   A CATEGORY ICON IS A SECTION THROUGH THE DEFECT, not a pictogram of the word.
   Nine categories in a list of codes read as nine similar words; drawn as what
   the surface looks like — a chip out of the face, bars showing, a stepped
   joint — the one wanted is found by eye before the code is read.

   SEVERITY IS BARS, rising, one per step. Severity is an ordering (see .tag--1…4
   in app.css), and four unrelated glyphs would read as four kinds of thing.
   --------------------------------------------------------------------------- */

import type { IconPath } from '../core/dom.ts';
import type { DefectSeverity } from '../data/defects.ts';

export const DEFECT_ICONS: Readonly<Record<string, IconPath>> = {
  // A face with flakes lifting off it.
  SCA: ['M3 18h18', 'M4 14l2.5-2 2.5 2', 'M10.5 14l2.5-2 2.5 2', 'M17 14l2-1.5 2 1.5', 'M6 8.5h1M12 7.5h1M18 8.5h1'],
  // A chunk broken out of the face, the piece falling away.
  SPA: ['M3 7h5l2 5h4l2-5h5', 'M3 18h18', 'M11 16.5l1.5-1.5 1.5 1.5'],
  // A slab cut open with the bars showing.
  REE: ['M3 6h18v12H3z', 'M8 12m-1.5 0a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0', 'M16 12m-1.5 0a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0'],
  // A dish in the running surface.
  POH: ['M2 10h5', 'M17 10h5', 'M7 10c0 5 10 5 10 0', 'M9.5 11.5h5'],
  // Two units with the gap between them opening.
  SEP: ['M3 7h6v10H3', 'M21 7h-6v10h6', 'M10.5 12h3', 'M11.5 10.5 10 12l1.5 1.5', 'M12.5 10.5 14 12l-1.5 1.5'],
  // A pier in the river, the bed carried away around its foot.
  SCO: ['M9 3v13', 'M15 3v13', 'M2 12c1.7-1.3 3.3-1.3 5 0', 'M17 12c1.7-1.3 3.3-1.3 5 0', 'M4 20c2.5-3 13.5-3 16 0'],
  // One unit dropped below its neighbour.
  SET: ['M3 7h8v6H3z', 'M13 11h8v6h-8z', 'M17 3v5', 'M15.5 6.5 17 8l1.5-1.5'],
  // A crack running through the member.
  CRK: ['M3 5h18v14H3z', 'M13 5l-2.5 4 3 2.5-3 3.5 1.5 4'],
  // An embankment washed back, particles carried off.
  ERO: ['M3 20h18', 'M3 20c3-7 7-11 13-11h5', 'M15 14h.5M18 16h.5M13 17h.5'],
};

/** Bars, rising with the severity. Minor has one, Critical four. */
export const SEVERITY_ICONS: Readonly<Record<DefectSeverity, IconPath>> = {
  Minor: ['M5 19v-3'],
  Moderate: ['M5 19v-3', 'M10 19v-6'],
  Severe: ['M5 19v-3', 'M10 19v-6', 'M15 19v-9'],
  Critical: ['M5 19v-3', 'M10 19v-6', 'M15 19v-9', 'M20 19v-12'],
};
